import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { isAuth, userData } from 'selectors/user';
import { FormattedMessage } from 'react-intl';
import SmallCenteredPaper from 'components/templates/SmallCenteredPaper';
import * as Sentry from '@sentry/browser';
import { Button, Typography } from '@material-ui/core';
import {
  fetchCancellationPolicyRequest,
  fetchUserSelfRequest,
  fetchSelfEvents,
  fetchTodos,
  fetchSessionStatusRequest,
} from '../actions/index';

const LOAD_TIMEOUT = 300;

class Startup extends React.Component {
  state = {
    error: false,
    isLoading: true,
  };

  componentDidMount() {
    this.checkInit();
    this.props.fetchUserSelfRequest();
    this.props.fetchSessionStatusRequest();
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // Try to fetch user again if he've lost his session
    if (!nextProps.isAuth && this.props.isAuth) {
      this.props.fetchUserSelfRequest();
    }

    if (nextProps.isAuth && !this.props.isAuth) {
      this.props.fetchCancellationPolicyRequest();
      this.props.fetchSelfEvents();
      this.props.fetchTodos();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.location !== prevProps.location) {
      window.scrollTo(0, 0);
    }
  }

  componentDidCatch(error, errorInfo) { // eslint-disable-line class-methods-use-this
    this.setState({ error });
    const { user } = this.props;

    if (process.env.NODE_ENV !== 'test' && process.env.REACT_APP_SENTRY_URL) {
      Sentry.withScope((scope) => {
        scope.setExtras(errorInfo);

        if (user) {
          scope.setUser({
            id: user.id,
            username: user.username,
          });
        }

        Sentry.captureException(error);
      });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.props.fetchSessionStatusRequest();
    }
  };

  checkInit = () => {
    const { google } = window;

    if (google) {
      this.setState({ isLoading: false });
      document.getElementById('initloader').classList.add('initialized');
    } else {
      setTimeout(this.checkInit, LOAD_TIMEOUT);
    }
  }

  render() {
    const {
      error,
      isLoading,
    } = this.state;

    if (isLoading && !error) {
      return null;
    }

    return error ? (
      <SmallCenteredPaper>
        <Typography
          paragraph
          color="secondary"
        >
          <FormattedMessage
            id="globalInfo.error"
            defaultMessage="We have some application problems,
            please reload app or contact support if you see this message again."
          />
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          component="a"
          href="mailto:info@stugknuten.com"
        >
          <FormattedMessage
            id="Contact support"
            defaultMessage="Contact support"
          />
        </Button>
      </SmallCenteredPaper>
    ) : (
      <>{this.props.children}</>
    );
  }
}

Startup.propTypes = {
  children: PropTypes.node.isRequired,
  isAuth: PropTypes.bool,
  user: PropTypes.shape({
    id: PropTypes.number,
    username: PropTypes.string,
  }),
  location: PropTypes.shape({}),

  fetchCancellationPolicyRequest: PropTypes.func.isRequired,
  fetchUserSelfRequest: PropTypes.func.isRequired,
  fetchTodos: PropTypes.func.isRequired,
  fetchSelfEvents: PropTypes.func.isRequired,
  fetchSessionStatusRequest: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  isAuth: isAuth(state),
  user: userData(state),
});

const mapDispatchToProps = {
  fetchCancellationPolicyRequest,
  fetchUserSelfRequest,
  fetchSelfEvents,
  fetchTodos,
  fetchSessionStatusRequest,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Startup));
